import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "besca_token";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
};

export const api = axios.create({ baseURL: API, withCredentials: true });

// Attach the JWT as a Bearer header when available. This keeps the session
// working in browsers that block the httpOnly cookie (privacy mode, Safari ITP,
// third-party cookie blocking). The httpOnly cookie remains the primary path.
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authRegister = async (payload) => {
  const data = (await api.post("/auth/register", payload)).data;
  if (data?.token) setToken(data.token);
  return data;
};

// Weiterempfehlung (Referral)
export const referralStatus = async () => (await api.get("/referrals/status")).data;
export const referralInvite = async (email) =>
  (await api.post("/referrals/invite", { email })).data;
export const referralResend = async (email) =>
  (await api.post("/referrals/resend", { email })).data;
export const authLogin = async (email, password) => {
  const data = (await api.post("/auth/login", { email, password })).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const authTwoFactorVerify = async (challengeId, code) => {
  const data = (await api.post("/auth/2fa/verify", { challenge_id: challengeId, code })).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const twoFactorEnableRequest = async () =>
  (await api.post("/auth/2fa/enable/request")).data;
export const twoFactorEnableConfirm = async (challengeId, code) =>
  (await api.post("/auth/2fa/enable/confirm", { challenge_id: challengeId, code })).data;
export const twoFactorDisable = async (password) =>
  (await api.post("/auth/2fa/disable", { password })).data;
export const authLogout = async () => {
  try {
    return (await api.post("/auth/logout")).data;
  } finally {
    setToken(null);
  }
};
export const authMe = async () => (await api.get("/auth/me")).data;

// Double opt-in email verification
export const authVerifyEmail = async (token) => {
  const data = (await api.post("/auth/verify-email", { token })).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const authResendVerification = async (email) =>
  (await api.post("/auth/resend-verification", { email })).data;

// Organisation / Mandantenfähigkeit
export const orgRegister = async (payload) => {
  const data = (await api.post("/auth/register-org", payload)).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const orgCreateInvitation = async (email, role) =>
  (await api.post("/org/invitations", { email, role })).data;
export const orgListInvitations = async () => (await api.get("/org/invitations")).data;
export const orgRevokeInvitation = async (id) =>
  (await api.delete(`/org/invitations/${id}`)).data;
export const orgResendInvitation = async (id) =>
  (await api.post(`/org/invitations/${id}/resend`)).data;
export const orgListMembers = async () => (await api.get("/org/members")).data;
export const orgGetInvitation = async (token) =>
  (await api.get(`/org/invitation/${token}`)).data;
export const orgAcceptInvitation = async (token, password, name) => {
  const data = (await api.post("/org/invitation/accept", { token, password, name })).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const orgListConsents = async () => (await api.get("/org/consents")).data;
export const orgConsentDecision = async (token, decision) =>
  (await api.post("/org/consent", { token, decision })).data;

// Phase 2b – Zugriff & Operationen
export const orgOpenAccount = async (id) => {
  const data = (await api.post(`/org/open/${id}`)).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const orgStopOpen = async () => {
  const data = (await api.post(`/org/stop-open`)).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const orgSetMemberPermission = async (id, payload) =>
  (await api.post(`/org/members/${id}/permission`, payload)).data;
export const orgDecouple = async () => (await api.post(`/org/decouple`)).data;
export const orgUpgradeToUnternehmer = async () => {
  const data = (await api.post(`/org/upgrade-to-unternehmer`)).data;
  return data;
};

// Aktivitätsprotokoll (Phase 3)
export const activityList = async (params = {}) => (await api.get("/activity", { params })).data;
export const activityCsv = async (params = {}) =>
  api.get("/activity/export", { params, responseType: "blob" });

// Admin
export const adminListUsers = async () => (await api.get("/admin/users")).data;
export const adminGetKpis = async () => (await api.get("/admin/kpis")).data;
export const adminWeeklyTrend = async (period = "weeks") =>
  (await api.get("/admin/weekly-trend", { params: { period } })).data;
export const adminDeactivateUser = async (id) =>
  (await api.post(`/admin/users/${id}/deactivate`)).data;
export const adminReactivateUser = async (id) =>
  (await api.post(`/admin/users/${id}/reactivate`)).data;
export const adminSetFreeAccess = async (id, enable) =>
  (await api.post(`/admin/users/${id}/free-access`, { enable })).data;
export const adminExportUser = async (id) =>
  api.get(`/admin/users/${id}/export`, { responseType: "blob" });
export const adminDeleteUser = async (id) =>
  (await api.delete(`/admin/users/${id}`)).data;
export const adminDeletionLog = async () =>
  (await api.get("/admin/deletion-log")).data;
export const adminDeletionLogCsv = async (params = {}) =>
  api.get("/admin/deletion-log/export", { params, responseType: "blob" });
export const adminImpersonate = async (id) => {
  const data = (await api.post(`/admin/impersonate/${id}`)).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const adminStopImpersonation = async () => {
  const data = (await api.post(`/admin/stop-impersonation`)).data;
  if (data?.token) setToken(data.token);
  return data;
};
export const adminListFeatures = async () => (await api.get("/admin/features")).data;
export const adminDismissFeature = async (key) =>
  (await api.post(`/admin/features/${key}/dismiss`)).data;
export const updateProfile = async (payload) =>
  (await api.put("/auth/me", payload)).data;
export const changePassword = async (current_password, new_password) =>
  (await api.post("/auth/change-password", { current_password, new_password })).data;
export const forgotPassword = async (email) =>
  (await api.post("/auth/forgot-password", { email })).data;
export const resetPassword = async (token, new_password) =>
  (await api.post("/auth/reset-password", { token, new_password })).data;
export const deleteAccount = async () => {
  try {
    return (await api.delete("/auth/account")).data;
  } finally {
    setToken(null);
  }
};

// Stammdaten / Listen
export const fetchLists = async () => (await api.get("/lists")).data;
export const addListOption = async (type, value) =>
  (await api.post(`/lists/${type}`, { value })).data;
export const deleteListOption = async (id) =>
  (await api.delete(`/lists/${id}`)).data;

// Belege
export const extractReceipt = async (image_base64, image_mime) =>
  (await api.post("/receipts/extract", { image_base64, image_mime })).data;
export const createReceipt = async (payload) =>
  (await api.post("/receipts", payload)).data;
export const updateReceipt = async (id, payload) =>
  (await api.put(`/receipts/${id}`, payload)).data;
export const getReceipt = async (id) => (await api.get(`/receipts/${id}`)).data;
export const listReceipts = async () => (await api.get("/receipts")).data;
export const deleteReceipt = async (id) =>
  (await api.delete(`/receipts/${id}`)).data;
export const getSummary = async () => (await api.get("/receipts/summary")).data;
export const pdfUrl = (id) => `${API}/receipts/${id}/pdf`;

// Exports (Sammel-PDF / ZIP / CSV) + letzter Export
export const getLastExports = async () => (await api.get("/exports/last")).data;
export const exportDownloads = async () => (await api.get("/exports/downloads")).data;

export const exportBundle = async (from, to, format = "pdf") =>
  api.get("/exports/bundle", {
    params: { from: from || undefined, to: to || undefined, format },
    responseType: "blob",
  });

export const exportCsv = async (from, to) =>
  api.get("/exports/csv", {
    params: { from: from || undefined, to: to || undefined },
    responseType: "blob",
  });

export const exportPackage = async (from, to) =>
  api.get("/exports/package", {
    params: { from: from || undefined, to: to || undefined },
    responseType: "blob",
  });

export const filenameFromResponse = (res, fallback) => {
  const cd = res?.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  return m ? m[1] : fallback;
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  // Delay cleanup so the browser can actually start the download before the
  // object URL is revoked (revoking immediately cancels the download in Chrome).
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 2000);
};

// Abo / Zahlung (Stripe)
export const billingStatus = async () => (await api.get("/billing/status")).data;
export const billingCheckout = async (plan, origin) =>
  (await api.post("/billing/checkout", { plan, origin })).data;
export const billingPortal = async (origin) =>
  (await api.post("/billing/portal", { origin })).data;
export const billingSyncSession = async (sessionId) =>
  (await api.get(`/billing/session/${sessionId}`)).data;

// Credits / Wallet (Phase 4 – Mandantenfähigkeit)
export const creditsStatus = async () => (await api.get("/credits/status")).data;
export const creditsPricing = async () => (await api.get("/credits/pricing")).data;

// --- Handbuch ---------------------------------------------------------------
export const handbookGet = async () => (await api.get("/handbook")).data;
export const handbookAdminSections = async () =>
  (await api.get("/admin/handbook/sections")).data;
export const handbookCreateSection = async (payload) =>
  (await api.post("/admin/handbook/sections", payload)).data;
export const handbookUpdateSection = async (id, payload) =>
  (await api.put(`/admin/handbook/sections/${id}`, payload)).data;
export const handbookDeleteSection = async (id) =>
  (await api.delete(`/admin/handbook/sections/${id}`)).data;
export const handbookReorder = async (ids) =>
  (await api.post("/admin/handbook/reorder", { ids })).data;
export const handbookPending = async () => (await api.get("/admin/handbook/pending")).data;
export const handbookPendingCount = async () =>
  (await api.get("/admin/handbook/pending/count")).data;
export const handbookApprove = async (id) =>
  (await api.post(`/admin/handbook/pending/${id}/approve`)).data;
export const handbookReject = async (id) =>
  (await api.post(`/admin/handbook/pending/${id}/reject`)).data;
export const handbookSectionVersions = async (id) =>
  (await api.get(`/admin/handbook/sections/${id}/versions`)).data;
export const handbookSectionRestore = async (id, versionId) =>
  (await api.post(`/admin/handbook/sections/${id}/restore/${versionId}`)).data;
export const handbookSectionVersionDelete = async (id, versionId) =>
  (await api.delete(`/admin/handbook/sections/${id}/versions/${versionId}`)).data;
export const handbookUploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (
    await api.post("/admin/handbook/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  ).data;
};
export const creditsCheckout = async (pkg, origin) =>
  (await api.post("/credits/checkout", { package: pkg, origin })).data;
export const creditsSession = async (sessionId) =>
  (await api.get(`/credits/session/${sessionId}`)).data;
export const creditsSetCountry = async (country) =>
  (await api.post("/credits/billing-country", { country })).data;
export const creditsTransactions = async () => (await api.get("/credits/transactions")).data;
export const creditsTransactionsCsv = async () =>
  api.get("/credits/transactions/export", { responseType: "blob" });
export const creditsInvoicePdf = async (number) =>
  api.get(`/credits/invoice/${number}`, { responseType: "blob" });
export const creditsInvoicesZip = async (params) =>
  api.get("/credits/invoices/export", { params, responseType: "blob" });
export const autoRechargeSetup = async (origin) =>
  (await api.post("/credits/auto-recharge/setup", { origin })).data;
export const autoRechargeConfirm = async (sessionId) =>
  (await api.get(`/credits/auto-recharge/confirm/${sessionId}`)).data;
export const autoRechargeSave = async (settings) =>
  (await api.post("/credits/auto-recharge", settings)).data;

// --- E-Mail-Vorlagen (Admin) ---
export const emailTemplatesList = async () =>
  (await api.get("/admin/email-templates")).data;
export const emailTemplatesNewCount = async () =>
  (await api.get("/admin/email-templates/new-count")).data;
export const emailTemplatesMarkSeen = async () =>
  (await api.post("/admin/email-templates/mark-seen")).data;
export const emailTemplateGet = async (key) =>
  (await api.get(`/admin/email-templates/${key}`)).data;
export const emailTemplateSave = async (key, payload) =>
  (await api.put(`/admin/email-templates/${key}`, payload)).data;
export const emailTemplateReset = async (key) =>
  (await api.post(`/admin/email-templates/${key}/reset`)).data;
export const emailTemplateSendTest = async (key, payload) =>
  (await api.post(`/admin/email-templates/${key}/send-test`, payload)).data;
export const emailTemplateVersions = async (key) =>
  (await api.get(`/admin/email-templates/${key}/versions`)).data;
export const emailTemplatePreview = async (key, payload) =>
  (await api.post(`/admin/email-templates/${key}/preview`, payload)).data;
export const emailTemplateRestore = async (key, versionId) =>
  (await api.post(`/admin/email-templates/${key}/restore/${versionId}`)).data;
export const emailTemplateVersionDelete = async (key, versionId) =>
  (await api.delete(`/admin/email-templates/${key}/versions/${versionId}`)).data;

// WALTER – Support-Assistent
export const walterChat = async (sessionId, message) =>
  (await api.post("/support/chat", { session_id: sessionId, message })).data;
export const walterHistory = async (sessionId) =>
  (await api.get("/support/history", { params: { session_id: sessionId } })).data;
export const adminListSupportFacts = async () =>
  (await api.get("/admin/support-facts")).data.facts;
export const adminCreateSupportFact = async (payload) =>
  (await api.post("/admin/support-facts", payload)).data;
export const adminUpdateSupportFact = async (id, payload) =>
  (await api.put(`/admin/support-facts/${id}`, payload)).data;
export const adminDeleteSupportFact = async (id) =>
  (await api.delete(`/admin/support-facts/${id}`)).data;
export const adminListUnanswered = async () =>
  (await api.get("/admin/support-unanswered")).data.questions;
export const adminAnswerUnanswered = async (id, payload) =>
  (await api.post(`/admin/support-unanswered/${id}/answer`, payload)).data;
export const adminDismissUnanswered = async (id) =>
  (await api.post(`/admin/support-unanswered/${id}/dismiss`)).data;
export const adminUnansweredCount = async () =>
  (await api.get("/admin/support-unanswered/count")).data.count;


